import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  List,
  required,
  SelectInput,
  useListFilterContext,
} from 'react-admin';

import {
  Card,
  Divider,
  Tooltip,
} from '@mui/material';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { DateTime } from 'luxon';
import { SubListNavigation } from '../layout/SubNavigation';
import { StyledTab, StyledTabs } from '../layout/Tab';
import Pagination from '../layout/Pagination';

import requestTypesChoices, { requestTypes, RequestTypes } from '../../constants/requestTypes';
import { requestListMap } from '../../constants/requestFeatures';
import EntityFilter from '../filters/EntityFilter';
import recommendations, { Recommendations } from '../../constants/recommendations';
import SmartDateTimeInput from '../../customFields/SmartDateTimeInput';
import SearchField from '../../customFields/SearchField';
import dateParse, { dateFormat } from '../utils/dateParser';

const defaultFilterValues = { requestType: requestTypes.TRANSACTION };

const stoppedDescriptionId = 'requests-stopped-description';

type RequestFilters = {
  requestType: RequestTypes;
  'custom.entity'?: string[];
  recommendation?: Recommendations;
  processStopped: boolean;
  requestFrom?: string;
  requestTo?: string;
};

export const RequestListBody = ({ children }: { children?: React.ReactNode }) => {
  const { filterValues, setFilters } = useListFilterContext();
  const { requestType } = filterValues as RequestFilters;

  useEffect(() => {
    if (!requestType) {
      setFilters({ ...filterValues, ...defaultFilterValues });
    }
  }, [filterValues, requestType, setFilters]);

  const RequestListComponent = requestListMap[requestType];
  if (!RequestListComponent) return null;

  return <RequestListComponent>{children}</RequestListComponent>;
};

const dateRangeValidation = (_value: string, allValues: RequestFilters) => {
  const { requestFrom, requestTo } = allValues;
  if (
    requestFrom
    && requestTo
  ) {
    if (DateTime.fromISO(requestFrom) > DateTime.fromISO(requestTo)) {
      return 'Invalid date range';
    }
    if (DateTime.fromISO(requestFrom).plus({ years: 5 }) < (DateTime.fromISO(requestTo))) {
      return 'Range greater than 5 years';
    }
  }
  return undefined;
};

const isPastDate = (value: string) => {
  if (DateTime.fromISO(value) > DateTime.now()) return 'Invalid date';
  return undefined;
};

export const getRequestFilters = ({
  hideSearch = false,
  availableRequestTypes = Object.values(requestTypes),
}) => [
  <SelectInput
    alwaysOn
    source="requestType"
    choices={Object.values(requestTypesChoices)
      .filter(({ id }) => availableRequestTypes.includes(id))}
    validate={required()}
  />,
  ...(hideSearch ? [] : [<SearchField alwaysOn label="Search" source="search" />]),
  <EntityFilter label="Entities" source="custom.entity" />,
  <SmartDateTimeInput
    label="From Date"
    source="requestFrom"
    validate={[isPastDate, dateRangeValidation]}
    parse={dateParse}
    format={dateFormat}
    sx={{
      '& .MuiFormHelperText-root': {
        display: 'unset',
      },
    }}
  />,
  <SmartDateTimeInput
    label="To Date"
    source="requestTo"
    validate={[isPastDate, dateRangeValidation]}
    parse={dateParse}
    format={dateFormat}
    sx={{
      '& .MuiFormHelperText-root': {
        display: 'unset',
      },
    }}
  />,
];

const RequestList = () => {
  const [recommendationTab, setRecommendationTab] = useState(0);
  const [
    recommendation,
    setRecommendationFilter,
  ] = useState<Recommendations | undefined>(undefined);
  const [processStopped, setProcessStopped] = useState(false);
  const [stoppedDescriptionOpen, setStoppedDescriptionOpen] = useState(false);
  const stoppedTabRef = useRef<HTMLDivElement>(null);
  const openStoppedDescription = useCallback(() => setStoppedDescriptionOpen(true), []);
  // Hover and keyboard focus each keep the explanation open on their own:
  // leaving with the pointer does not close it while the tab has keyboard focus,
  // and blurring does not close it while the pointer is still over the tab.
  // Escape always closes it.
  const closeStoppedDescription = useCallback((event: React.SyntheticEvent | Event) => {
    const tab = stoppedTabRef.current;
    if (event.type === 'mouseleave' && tab?.matches(':focus-visible')) return;
    if (event.type === 'blur' && tab?.matches(':hover')) return;
    setStoppedDescriptionOpen(false);
  }, []);

  const handleChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setRecommendationTab(newValue);
    if (newValue === 0) {
      setRecommendationFilter(undefined);
      setProcessStopped(false);
    }
    if (newValue === 1) {
      setRecommendationFilter(recommendations.REVIEW);
      setProcessStopped(false);
    }
    if (newValue === 2) {
      setRecommendationFilter(recommendations.PROCEED);
      setProcessStopped(false);
    }
    if (newValue === 3) {
      setRecommendationFilter(recommendations.BLOCK);
      setProcessStopped(false);
    }
    if (newValue === 4) {
      setProcessStopped(true);
      setRecommendationFilter(undefined);
    }
  }, []);

  return (
    <List
      filter={{ recommendation, processStopped }}
      empty={false}
      pagination={<Pagination disableEmptyText />}
      filterDefaultValues={defaultFilterValues}
      filters={getRequestFilters({})}
      sort={{ field: 'createdAt', order: 'DESC' }}
      exporter={false}
      perPage={50}
    >
      <Card sx={{ margin: 0 }} variant="outlined">
        <Card sx={{ margin: 0 }}>
          <SubListNavigation>
            <StyledTabs value={recommendationTab} onChange={handleChange}>
              <StyledTab label="All" />
              <StyledTab label="Review " />
              <StyledTab label="Proceed" />
              <StyledTab label="Block" />
              {/* The tooltip wraps the whole tab, so the tab itself opens the
                  explanation and carries it as its accessible description. */}
              <Tooltip
                id={stoppedDescriptionId}
                title="Requests that have not been correctly processed"
                open={stoppedDescriptionOpen}
                onOpen={openStoppedDescription}
                onClose={closeStoppedDescription}
                describeChild
                slotProps={{ popper: { keepMounted: true } }}
              >
                <StyledTab
                  ref={stoppedTabRef}
                  icon={<InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} aria-hidden />}
                  iconPosition="end"
                  label="Stopped"
                  aria-describedby={stoppedDescriptionId}
                />
              </Tooltip>
            </StyledTabs>
          </SubListNavigation>
          <Divider />
        </Card>
        <RequestListBody />
      </Card>
    </List>
  );
};

export default RequestList;
